import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { Cookies } from 'react-cookie';

class HttpClient {
  private instance: AxiosInstance;
  private cookies: Cookies;
  private preLoginToken: string | null;

  constructor(baseURL: string, timeout = 30000) {
    this.instance = axios.create({
      baseURL,
      timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.cookies = new Cookies();
    this.preLoginToken = null;
    this.addInterceptors();
  }

  // Method to fetch and set pre-login token
  public async initializePreLoginToken(apiUrl: string): Promise<void> {
    try {
      const response = await this.instance.post(apiUrl);
      this.preLoginToken = response?.data?.token; // Assuming the token is in the response
      console.log('Pre-login token fetched and set:', this.preLoginToken);
    } catch (error) {
      console.error('Error fetching pre-login token:', error);
      // If token generation fails, set a dummy token for now to prevent further issues
      this.preLoginToken = 'dummy_guest_token';
    }
  }

  // Method to clear tokens (useful on logout)
  public clearTokens() {
    this.preLoginToken = null;
    this.cookies.remove('token');
    this.cookies.remove('organization_id'); // Clear organization_id
    this.cookies.remove('organization_role'); // Clear organization_role
  }

  private addInterceptors() {
    // Request Interceptor
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        console.log('[Request]', config);

        // Drop the JSON default Content-Type for FormData so axios/browser
        // can set multipart/form-data with the correct boundary.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          if (config.headers) {
            delete (config.headers as any)['Content-Type'];
            delete (config.headers as any)['content-type'];
          }
        }

        // Don't add token for login/signup
        if (config.url === '/api/users/login/' || config.url === '/api/users/register/') {
          return config;
        }

        const token = this.cookies.get('token'); // Post-login token
        console.log('Token from cookie:', token);
        if (token) {
          config.headers.Authorization = `Token ${token}`;
        }

        return config;
      },
      (error) => {
        console.error('[Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response Interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log('[Response]', response);
        return response;
      },
      (error) => {
        console.error('[Response Error]', error);
        const data = error.response?.data;
        const customError = {
          status: error.response?.status,
          message:
            data?.message ||
            data?.error ||
            data?.detail ||
            (typeof data === 'string' ? data : null) ||
            'An unexpected error occurred',
          data,
        };
        return Promise.reject(customError);
      }
    );
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  // Payment API calls
  public async createOrder(bookingId: string): Promise<any> {
    return this.post(`${this.instance.defaults.baseURL}/api/payments/payment/create-order/`, { booking_id: bookingId });
  }

  public async confirmPayment(paymentData: any): Promise<any> {
    return this.post(`${this.instance.defaults.baseURL}/api/payments/payment/confirm-payment/`, paymentData);
  }

  // Wallet API calls
  public async getWalletData(): Promise<any> {
    return this.get(`${this.instance.defaults.baseURL}/api/payments/wallet/`);
  }

  public async getWalletTransactions(): Promise<any> {
    return this.get(`${this.instance.defaults.baseURL}/api/payments/wallet/transactions/`);
  }

  public async forgotPassword(email: string): Promise<any> {
    return this.post('/api/users/forgot-password/', { email });
  }

  public async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<any> {
    return this.post('/api/users/reset-password/', { token, new_password: newPassword, confirm_password: confirmPassword });
  }

  public async sendLoginOtp(phoneNumber: string): Promise<any> {
    return this.post('/api/users/send-login-otp/', { phone_number: phoneNumber });
  }

  public async resetPasswordByOtp(phoneNumber: string, otp: string, newPassword: string, confirmPassword: string): Promise<any> {
    return this.post('/api/users/reset-password-by-otp/', { phone_number: phoneNumber, otp, new_password: newPassword, confirm_password: confirmPassword });
  }
}

// Factory function to initialize the client
const createHttpClient = async (baseURL: string): Promise<HttpClient> => {
  const client = new HttpClient(baseURL);
  await client.initializePreLoginToken('/api/users/generate-guest-token/');
  return client;
};

const apiClient = createHttpClient(process.env.REACT_APP_API_ENDPOINT!);
const userApiClient = createHttpClient(process.env.REACT_APP_USER_SERVICE_API_ENDPOINT!);

export { HttpClient, createHttpClient, apiClient, userApiClient };